import React from 'react';
import PropTypes from 'prop-types';

import { compose, mapProps } from '@shakacode/recompose';
import { closestElement } from '../../utils/dom';
import { withWindowSizeContext } from '../../shared/WindowSizeProvider';

const CALENDAR_EVENT_MOBILE_PREVIEW_STYLES = `
  @media only screen and (max-width: 480px) {
    .calendarEventPreviewContent {
      table-layout: fixed !important;
    }

    .calendarEventPreviewContent h4 {
      font-size: 14px !important;
      overflow-wrap: anywhere !important;
      word-break: break-word !important;
    }
  }
`;

class MessagePreview extends React.PureComponent {
  constructor(props) {
    super(props);
    this.frameContainerRef = React.createRef();
    this.writeIframeContent = this.writeIframeContent.bind(this);
  }

  componentDidMount() {
    this.initFrame();
  }

  componentDidUpdate() {
    this.initFrame();
  }

  initFrame() {
    if (!this.frameContainerRef.current) {
      return;
    }

    // Replace container contents with new iframe
    while (this.frameContainerRef.current.firstChild) {
      this.frameContainerRef.current.removeChild(this.frameContainerRef.current.firstChild);
    }
    const iframe = document.createElement('iframe');
    iframe.style.width = '100%';
    iframe.style.display = 'block';
    iframe.title = 'message-preview-iframe';
    if (!this.props.autoContentHeight) {
      iframe.style.height = `${this.props.windowHeight * this.props.windowHeightPercent}px`;
      iframe.height = `${this.props.windowHeight * this.props.windowHeightPercent}`;
    }

    if (this.props.hideBorders) {
      iframe.style.border = '0';
    }

    iframe.addEventListener('load', () => {
      if (this.props.autoContentHeight) {
        const contentHeight = iframe.contentWindow.document.body.scrollHeight;
        iframe.style.height = `${contentHeight}px`;
        iframe.height = `${contentHeight}`;
      } else if (this.props.fullHeight) {
        const contentHeight = iframe.contentWindow.document.body.scrollHeight;
        iframe.style.height = '100%';
        iframe.height = `${contentHeight}`;
      }

      iframe.contentWindow.document.body.addEventListener('click', (e) => {
        const link = closestElement(e.target, 'a');
        if (link && link.href) {
          e.preventDefault();
          e.stopPropagation();
          window.open(link.href, '_blank');
          window.focus();
          return false;
        }
        return true;
      });
    });

    if (!(this.props.messageContentHtml || this.props.messageHtmlUrl)) return;

    let htmlContent = this.props.messageContentHtml;
    const personalizations = this.props.messageHtmlPersonalization;
    if (personalizations) {
      const { substitutions } = personalizations;
      if (htmlContent) {
        htmlContent = this.replaceContent(htmlContent, substitutions);
        this.writeIframeContent(iframe, htmlContent);
      } else {
        fetch(this.props.messageHtmlUrl)
          .then(response => response.text())
          .then((content) => {
            this.writeIframeContent(iframe, this.replaceContent(content, substitutions));
          });
      }
    } else if (this.props.messageHtmlUrl) {
      iframe.src = this.props.messageHtmlUrl;
      this.frameContainerRef.current.appendChild(iframe);
    } else {
      this.writeIframeContent(iframe, htmlContent);
    }
  }

  writeIframeContent(iframe, content) {
    this.frameContainerRef.current.appendChild(iframe);
    iframe.contentWindow.document.open();
    iframe.contentWindow.document.write(content);
    iframe.contentWindow.document.close();

    if (!iframe.contentWindow.document.querySelector('.calendarEventPreviewContent')) return;

    const previewStyles = iframe.contentWindow.document.createElement('style');
    previewStyles.dataset.messagePreviewStyles = 'true';
    previewStyles.textContent = CALENDAR_EVENT_MOBILE_PREVIEW_STYLES;
    iframe.contentWindow.document.head.appendChild(previewStyles);
  }

  replaceContent(content, substitutions) {
    return Object.entries(substitutions).reduce(
      (replacedContent, [key, value]) => replacedContent.replace(new RegExp(key, 'g'), value),
      content,
    );
  }

  render() {
    let style = {
      width: '100%',
    };

    if (this.props.fullHeight) style = { ...style, height: '100%' };
    return (
      <div ref={this.frameContainerRef} style={style} data-testid="iframe-container" />
    );
  }
}

MessagePreview.defaultProps = {
  autoContentHeight: false,
  fullHeight: false,
  hideBorders: false,
  messageContentHtml: null,
  messageHtmlPersonalization: null,
  messageHtmlUrl: null,
  windowHeightPercent: 0.5,
};

MessagePreview.propTypes = {
  autoContentHeight: PropTypes.bool,
  fullHeight: PropTypes.bool,
  hideBorders: PropTypes.bool,
  messageContentHtml: PropTypes.string,
  messageHtmlPersonalization: PropTypes.object,
  messageHtmlUrl: PropTypes.string,
  windowHeight: PropTypes.number.isRequired,
  windowHeightPercent: PropTypes.number,
};

export default compose(
  withWindowSizeContext,
  mapProps(({ windowSize, ...props }) => ({
    ...props,
    windowHeight: windowSize.innerHeight,
  })),
)(MessagePreview);
